import React from 'react';
import { RiArrowDownSLine, RiArrowRightSLine } from 'react-icons/ri';
import { withTheme } from '../ThemeProvider';
import { StyledPanel, StyledPanelContent, StyledPanelHeader } from './styles';
import { PanelProps } from './types';

const PanelComponent: React.FC<PanelProps> = ({
  label,
  children,
  isActive = false,
  panelKey,
  expandIcon,
  expandIconPosition = 'end',
  showArrow = true,
  extra,
  collapsible = 'header',
  forceRender = false,
  onClick,
  destroyOnHidden = false,
  classNames,
  styles,
  className,
  style,
}: PanelProps) => {
  const defaultExpandIcon = isActive ? (
    <RiArrowDownSLine size={20} className="icon" />
  ) : (
    <RiArrowRightSLine size={20} className="icon" />
  );
  const icon = expandIcon ? expandIcon({ isActive, panelKey }) : defaultExpandIcon;

  const handleClick = () => {
    if (collapsible !== 'disabled' && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const renderHeader = () => {
    return (
      <StyledPanelHeader
        className={`${isActive ? 'active' : ''} ${classNames?.header || ''}`}
        onClick={collapsible === 'header' ? handleClick : undefined}
        onKeyDown={collapsible === 'header' ? handleKeyDown : undefined}
        tabIndex={collapsible === 'header' ? 0 : undefined}
        role={collapsible === 'header' ? 'button' : undefined}
        aria-expanded={isActive}
        style={{
          ...styles?.header,
        }}
      >
        <div className="flex items-center gap-2">
          {expandIconPosition === 'start' && showArrow && (
            <span
              className={collapsible === 'icon' ? 'cursor-pointer' : ''}
              onClick={
                collapsible === 'icon'
                  ? e => {
                      e.stopPropagation();
                      handleClick();
                    }
                  : undefined
              }
              onKeyDown={
                collapsible === 'icon'
                  ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClick();
                      }
                    }
                  : undefined
              }
              tabIndex={collapsible === 'icon' ? 0 : undefined}
              role={collapsible === 'icon' ? 'button' : undefined}
              aria-label="Toggle panel"
            >
              {icon}
            </span>
          )}
          <div className="font-medium">{label}</div>
        </div>
        <div className="flex items-center gap-2">
          {extra && <div>{extra}</div>}
          {expandIconPosition === 'end' && showArrow && (
            <span
              className={collapsible === 'icon' ? 'cursor-pointer' : ''}
              onClick={
                collapsible === 'icon'
                  ? e => {
                      e.stopPropagation();
                      handleClick();
                    }
                  : undefined
              }
              onKeyDown={
                collapsible === 'icon'
                  ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClick();
                      }
                    }
                  : undefined
              }
              tabIndex={collapsible === 'icon' ? 0 : undefined}
              role={collapsible === 'icon' ? 'button' : undefined}
              aria-label="Toggle panel"
            >
              {icon}
            </span>
          )}
        </div>
      </StyledPanelHeader>
    );
  };

  const renderContent = () => {
    if (!isActive && !forceRender && destroyOnHidden) {
      return null;
    }

    return (
      <StyledPanelContent
        className={classNames?.content || ''}
        style={{
          display: isActive || forceRender ? 'block' : 'none',
          ...styles?.content,
        }}
      >
        {children}
      </StyledPanelContent>
    );
  };

  return (
    <StyledPanel
      className={`${isActive ? 'active' : ''} ${className || ''}`}
      style={{
        ...style,
      }}
    >
      {renderHeader()}
      {renderContent()}
    </StyledPanel>
  );
};

export const Panel = withTheme(PanelComponent);
